/**
 * imageWatermark.js
 * 
 * Client-side utility for extracting capture timestamps from JPEG EXIF metadata
 * and burning an immutable, tamper-resistant verification HUD banner onto photos
 * prior to uploading to the server.
 * 
 * Zero external dependencies: uses HTML5 Canvas and native Uint8Array binary parsing.
 */

import { compressImageToMax1MB } from './imageCompressor.js';

/**
 * Format a Date object into a readable bilingual date-time string: DD/MM/YYYY HH:mm:ss
 * @param {Date|string|number} date
 * @param {boolean} [includeSeconds=true]
 * @returns {string}
 */
export function formatWatermarkDateTime(date, includeSeconds = true) {
  if (!date) return '-';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '-';

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');

  if (!includeSeconds) {
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
}

/**
 * Parse an EXIF date string (format "YYYY:MM:DD HH:MM:SS" or "YYYY-MM-DD HH:MM:SS")
 * @param {string} dateStr
 * @returns {Date|null}
 */
export function parseExifDateString(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const trimmed = dateStr.trim();
  // Standard EXIF format: "YYYY:MM:DD HH:MM:SS"
  const match = trimmed.match(/^(\d{4})[:\-](\d{2})[:\-](\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);
  const hour = parseInt(match[4], 10);
  const min = parseInt(match[5], 10);
  const sec = parseInt(match[6], 10);

  const parsed = new Date(year, month, day, hour, min, sec);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Extract EXIF DateTimeOriginal (tag 0x9003) or DateTime (tag 0x0132) from JPEG binary buffer.
 * Pure binary scanner without external libraries.
 * @param {ArrayBuffer|Uint8Array} buffer
 * @returns {Date|null}
 */
export function extractExifDate(buffer) {
  if (!buffer) return null;
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  if (bytes.length < 12) return null;

  // Must start with JPEG SOI marker 0xFFD8
  if (bytes[0] !== 0xFF || bytes[1] !== 0xD8) {
    return null;
  }

  let offset = 2;
  const length = bytes.length;

  while (offset < length - 4) {
    if (bytes[offset] !== 0xFF) {
      offset++;
      continue;
    }

    const marker = bytes[offset + 1];

    // Stop at Start of Scan (SOS) or EOI
    if (marker === 0xDA || marker === 0xD9) {
      break;
    }

    const segmentLength = (bytes[offset + 2] << 8) | bytes[offset + 3];
    if (segmentLength < 2 || offset + 2 + segmentLength > length) {
      break;
    }

    // APP1 Marker: 0xFFE1
    if (marker === 0xE1) {
      const exifHeaderOffset = offset + 4;
      // Check for "Exif\0\0" header (0x45, 0x78, 0x69, 0x66, 0x00, 0x00)
      if (
        bytes[exifHeaderOffset] === 0x45 &&
        bytes[exifHeaderOffset + 1] === 0x78 &&
        bytes[exifHeaderOffset + 2] === 0x69 &&
        bytes[exifHeaderOffset + 3] === 0x66 &&
        bytes[exifHeaderOffset + 4] === 0x00 &&
        bytes[exifHeaderOffset + 5] === 0x00
      ) {
        const tiffOffset = exifHeaderOffset + 6;
        const result = readExifDateFromTiff(bytes, tiffOffset, offset + 2 + segmentLength);
        if (result) return result;
      }
    }

    offset += 2 + segmentLength;
  }

  return null;
}

/**
 * Internal helper to read EXIF TIFF structure
 */
function readExifDateFromTiff(bytes, tiffOffset, maxOffset) {
  if (tiffOffset + 8 > maxOffset) return null;

  const isLittleEndian = bytes[tiffOffset] === 0x49 && bytes[tiffOffset + 1] === 0x49; // "II"
  const isBigEndian = bytes[tiffOffset] === 0x4D && bytes[tiffOffset + 1] === 0x4D; // "MM"
  if (!isLittleEndian && !isBigEndian) return null;

  function readUint16(o) {
    if (o + 2 > maxOffset) return 0;
    return isLittleEndian
      ? bytes[o] | (bytes[o + 1] << 8)
      : (bytes[o] << 8) | bytes[o + 1];
  }

  function readUint32(o) {
    if (o + 4 > maxOffset) return 0;
    return isLittleEndian
      ? (bytes[o] | (bytes[o + 1] << 8) | (bytes[o + 2] << 16) | (bytes[o + 3] << 24)) >>> 0
      : ((bytes[o] << 24) | (bytes[o + 1] << 16) | (bytes[o + 2] << 8) | bytes[o + 3]) >>> 0;
  }

  function readAscii(o, count) {
    let str = '';
    for (let i = 0; i < count; i++) {
      const idx = o + i;
      if (idx >= maxOffset || bytes[idx] === 0) break;
      str += String.fromCharCode(bytes[idx]);
    }
    return str;
  }

  const ifd0OffsetRel = readUint32(tiffOffset + 4);
  if (ifd0OffsetRel === 0 || tiffOffset + ifd0OffsetRel >= maxOffset) return null;

  let ifd0Pos = tiffOffset + ifd0OffsetRel;
  const numEntries0 = readUint16(ifd0Pos);
  ifd0Pos += 2;

  let exifSubIfdOffset = null;
  let dateTimeVal = null;

  for (let i = 0; i < numEntries0 && ifd0Pos + 12 <= maxOffset; i++) {
    const tag = readUint16(ifd0Pos);
    const count = readUint32(ifd0Pos + 4);
    const valueOffsetRel = readUint32(ifd0Pos + 8);

    if (tag === 0x8769) { // Exif IFD Pointer
      exifSubIfdOffset = tiffOffset + valueOffsetRel;
    } else if (tag === 0x0132) { // DateTime
      const valPos = count <= 4 ? (ifd0Pos + 8) : (tiffOffset + valueOffsetRel);
      dateTimeVal = readAscii(valPos, count);
    }

    ifd0Pos += 12;
  }

  // Check Exif SubIFD for DateTimeOriginal (0x9003) or DateTimeDigitized (0x9004)
  if (exifSubIfdOffset && exifSubIfdOffset + 2 <= maxOffset) {
    const numSubEntries = readUint16(exifSubIfdOffset);
    let subPos = exifSubIfdOffset + 2;

    for (let i = 0; i < numSubEntries && subPos + 12 <= maxOffset; i++) {
      const tag = readUint16(subPos);
      const count = readUint32(subPos + 4);
      const valueOffsetRel = readUint32(subPos + 8);

      if (tag === 0x9003 || tag === 0x9004) { // DateTimeOriginal or DateTimeDigitized
        const valPos = count <= 4 ? (subPos + 8) : (tiffOffset + valueOffsetRel);
        const dateStr = readAscii(valPos, count);
        const parsed = parseExifDateString(dateStr);
        if (parsed) return parsed;
      }
      subPos += 12;
    }
  }

  if (dateTimeVal) {
    const parsed = parseExifDateString(dateTimeVal);
    if (parsed) return parsed;
  }

  return null;
}

/**
 * Apply a tamper-resistant verification watermark onto a repair photo via HTML5 Canvas.
 * 
 * Burns:
 * - Capture Time & Date (From EXIF or live capture timestamp)
 * - Source Badge (🟢 ถ่ายสด หรือ 📁 คลังรูปภาพ)
 * - Ticket Number (e.g. FIX000053)
 * - Technician Name
 * - Location / Room
 * - SmartFix Verified security badge
 * 
 * @param {File|Blob} file
 * @param {Object} options
 * @param {string} [options.ticketNumber]
 * @param {string} [options.technicianName]
 * @param {string} [options.location]
 * @param {string} [options.source='camera'] 'camera' | 'gallery' | 'webcam'
 * @param {Date} [options.capturedAt]
 * @returns {Promise<File>} Compressed JPEG File with stamped watermark
 */
export async function applyTechnicianWatermark(file, options = {}) {
  if (!file || typeof window === 'undefined') return file;

  const {
    ticketNumber = '-',
    technicianName = '-',
    location = '-',
    source = 'camera',
  } = options;

  // 1. Determine REAL capture / creation date:
  // - If camera/webcam live: time of shutter press
  // - If gallery: EXIF DateTimeOriginal > file.lastModified on disk > fallback
  const isLive = options.isLive !== undefined
    ? Boolean(options.isLive)
    : (source === 'camera' || source === 'webcam' || Boolean(file && file.lastModified && (Date.now() - file.lastModified < 180000)));
  let photoDate = null;
  let exifDate = null;
  let isExif = false;
  let isFileDate = false;

  if (isLive) {
    photoDate = options.capturedAt instanceof Date ? options.capturedAt : new Date();
  } else {
    // Inspect JPEG EXIF
    try {
      const arrayBuffer = await file.arrayBuffer();
      exifDate = extractExifDate(arrayBuffer);
      if (exifDate && !isNaN(exifDate.getTime())) {
        photoDate = exifDate;
        isExif = true;
      }
    } catch (err) {
      // Graceful fallback
    }

    // If no EXIF (e.g. PNG, WebP, screenshot, or social media image without EXIF):
    // Read the actual modification/creation timestamp of the file from client disk
    if (!photoDate && file && file.lastModified) {
      const modDate = new Date(file.lastModified);
      if (!isNaN(modDate.getTime())) {
        photoDate = modDate;
        isFileDate = true;
      }
    }

    // Fallback if none exists
    if (!photoDate || isNaN(photoDate.getTime())) {
      photoDate = options.capturedAt instanceof Date ? options.capturedAt : new Date();
    }
  }

  // Check if file is older than 24 hours compared to now
  const isOldFile = !isLive && (Date.now() - photoDate.getTime() > 24 * 60 * 60 * 1000);

  // 2. Load file into HTML Image
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      // Keep object URL until loaded
      setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
      resolve(image);
    };
    image.onerror = (e) => {
      URL.revokeObjectURL(objectUrl);
      reject(e);
    };
    image.src = objectUrl;
  });

  const origWidth = img.naturalWidth || img.width;
  const origHeight = img.naturalHeight || img.height;

  // Set minimum resolution so HUD is always crisp
  const targetWidth = Math.max(origWidth, 1200);
  const scale = targetWidth / origWidth;
  const targetHeight = Math.round(origHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');

  // Draw original image
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  // 3. Draw Watermark HUD Banner at bottom
  const bannerHeight = Math.round(Math.max(targetHeight * 0.08, 64));
  const bannerY = targetHeight - bannerHeight;

  // Dark gradient HUD background
  const gradient = ctx.createLinearGradient(0, bannerY, 0, targetHeight);
  gradient.addColorStop(0, 'rgba(15, 23, 42, 0.88)');
  gradient.addColorStop(1, 'rgba(15, 23, 42, 0.96)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, bannerY, targetWidth, bannerHeight);

  // Top accent line
  ctx.fillStyle = '#38bdf8';
  ctx.fillRect(0, bannerY, targetWidth, 3);

  // Text formatting
  const paddingX = Math.round(targetWidth * 0.03);
  const fontSize = Math.round(bannerHeight * 0.38);

  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.textBaseline = 'middle';
  const textY = bannerY + Math.round(bannerHeight / 2) + 1;

  // Left: Ticket Number only (e.g. FIX000063)
  ctx.textAlign = 'left';
  ctx.fillStyle = '#ffffff';
  ctx.fillText(ticketNumber || '', paddingX, textY);

  // Right: Date & Time with label (e.g. เวลาของไฟล์ในเครื่อง: 19/06/2026 12:15:23 or Photo Time: 15/10/2026 00:30:15)
  let activeLang = options.lang || options.locale;
  if (!activeLang && typeof window !== 'undefined') {
    try {
      activeLang = localStorage.getItem('smartfix-locale') || (document.documentElement && document.documentElement.lang);
    } catch (e) {
      // Ignore
    }
  }
  const isEn = String(activeLang || '').toLowerCase().startsWith('en');
  const timeLabel = isLive
    ? (isEn ? 'Photo Time: ' : 'เวลาถ่ายรูป: ')
    : (isEn ? 'Device File Time: ' : 'เวลาของไฟล์ในเครื่อง: ');

  const formattedTime = formatWatermarkDateTime(photoDate, true);
  const timeDisplay = `${timeLabel}${formattedTime}`;

  // Ensure right text fits comfortably without colliding with ticketNumber on the left
  let rightFontSize = fontSize;
  ctx.font = `bold ${rightFontSize}px sans-serif`;
  const maxRightWidth = targetWidth - paddingX * 2 - (ticketNumber && ticketNumber !== '-' ? Math.round(targetWidth * 0.3) : 100);
  const measuredWidth = ctx.measureText(timeDisplay).width;
  if (measuredWidth > maxRightWidth && maxRightWidth > 50) {
    rightFontSize = Math.max(Math.floor(fontSize * (maxRightWidth / measuredWidth)), 12);
    ctx.font = `bold ${rightFontSize}px sans-serif`;
  }

  ctx.textAlign = 'right';
  ctx.fillStyle = '#f1f5f9';
  ctx.fillText(timeDisplay, targetWidth - paddingX, textY);

  // 4. Export Canvas to Blob & File
  const blob = await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.90);
  });

  if (!blob) return file;

  const baseFileName = file.name ? file.name.replace(/\.[^/.]+$/, '') : 'maintenance';
  const stampedFileName = `${baseFileName}_verified.jpg`;

  const stampedFile = new File([blob], stampedFileName, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });

  // Attach metadata for upload payload
  stampedFile.watermarkInfo = {
    photoTimestamp: photoDate.toISOString(),
    photoSource: source,
    isLiveCamera: isLive,
    isExif,
    isFileDate,
    isOldFile: false,
    displayTime: formattedTime,
    fullDisplayTime: timeDisplay,
    timeLabel,
    label: timeLabel,
    lang: isEn ? 'en' : 'th',
  };

  // 5. Run through 1MB compressor safety pipeline
  const finalFile = await compressImageToMax1MB(stampedFile);
  if (finalFile) {
    finalFile.watermarkInfo = stampedFile.watermarkInfo;
    return finalFile;
  }

  return stampedFile;
}
